import { Group, Stack, Text } from '@mantine/core';
import { type OptionConfig } from '@morph-mapper/node-logic';
import { Inputs } from '@morph-mapper/node-inputs';
import { OptionType } from '@morph-mapper/types';
import { t } from 'i18next';
import { ReferenceSelect } from '../reference-select';
import type { VariableOption } from '@/hooks';
import { BooleanInput } from '../../atoms/boolean-input/boolean-input';
import { CodeInput } from '../../atoms/code-input/code-input';
import { GraphOutputInput } from '../../atoms/graph-output-input/graph-output-input';
import { OptionTypeMenu } from '../../atoms/option-type-menu/option-type-menu';
import { match } from 'ts-pattern';

export type OptionFieldProps = {
  config: OptionConfig;
  value: unknown;
  type: OptionType;
  onChange: (value: unknown) => void;
  onTypeChange: (type: OptionType) => void;
  variables: VariableOption[];
  onOpenGraph?: () => void;
};

export const OptionField = ({
  config,
  value,
  type,
  onChange,
  onTypeChange,
  variables,
  onOpenGraph,
}: OptionFieldProps) => {
  const { title, description, input, selection } = config;
  const InputComponent = Inputs[input];

  return (
    <Stack spacing={8}>
      <Group position="apart" align="flex-start">
        <Stack spacing={2} style={{ flex: 1 }}>
          <Text fz="xs" fw={600}>
            {t(title)}
          </Text>
          {description && (
            <Text fz="xs" c="dimmed">
              {t(description)}
            </Text>
          )}
        </Stack>
        <OptionTypeMenu
          value={type}
          onChange={(type) => {
            onTypeChange(type);
            if (type === OptionType.Graph) {
              onOpenGraph && onOpenGraph();
            }
          }}
          allowedTypes={config.allowedOptionTypes}
        />
      </Group>

      {match(type)
        .with(OptionType.UserDefined, () => (
          <InputComponent
            value={value}
            handleChange={onChange}
            selection={selection ?? []}
          />
        ))
        .with(OptionType.Reference, () => (
          <ReferenceSelect
            data={variables}
            value={value as string | null}
            onChange={onChange}
          />
        ))
        .with(OptionType.Graph, () => (
          <GraphOutputInput
            value={value}
            onOpen={() => onOpenGraph && onOpenGraph()}
            onClear={() => onChange(null)}
          />
        ))
        .with(OptionType.Code, () => (
          <CodeInput value={value as string | undefined} onChange={onChange} />
        ))
        .with(OptionType.Boolean, () => (
          <BooleanInput value={value as boolean | null} onChange={onChange} />
        ))
        .exhaustive()}
    </Stack>
  );
};
